/**
 * /admin/share-tokens/:id — Per-Share-Token embed usage monitoring (J-A19).
 *
 * Shows last-7d Referer history + aggregates + out-of-allowlist flags. The
 * panel surfaces a direct revoke action when out-of-allowlist Referers are
 * detected (the existing DELETE /api/share-tokens/:id revoke flow).
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { adminApi, type ShareTokenUsagePayload } from '../../../state/admin/api.ts';
import { AdminLayout } from '../layout.tsx';
import { apiFetch } from '@/lib/api-client';
import { Badge, Button, Card, Heading } from '@/components/ui';
import { formatLocalDateTime } from '../../../lib/availability';

export function AdminShareTokenDetailPage(): ReactElement {
  const params = useParams<{ id: string }>();
  const tokenId = params.id ?? '';

  const [payload, setPayload] = useState<ShareTokenUsagePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  const reload = (): void => {
    void (async () => {
      setLoading(true);
      setError(null);
      const env = await adminApi.getShareTokenUsage(tokenId);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        setLoading(false);
        return;
      }
      setPayload(env.data);
      setLoading(false);
    })();
  };

  useEffect(() => {
    if (tokenId === '') return;
    reload();
    // reload is stable closure over tokenId; deliberate mount-driven effect.
    // eslint-disable-next-line
  }, [tokenId]);

  const onRevoke = (): void => {
    void (async () => {
      setRevoking(true);
      setError(null);
      const res = await apiFetch(`/api/share-tokens/${tokenId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok) {
        setError(`Revoke failed: HTTP ${String(res.status)}`);
        setRevoking(false);
        return;
      }
      setInfo('Token revoked. No further loads will be accepted.');
      setRevoking(false);
      reload();
    })();
  };

  const outOfAllowlist = payload?.summary.out_of_allowlist_hosts ?? [];
  const isRevoked = payload?.token.revoked_at !== null && payload?.token.revoked_at !== undefined;

  return (
    <AdminLayout title="Share Token" eyebrow="Admin · Share Token">
      {error !== null ? (
        <p role="alert" className="mt-4 font-sans text-small text-error">
          {error}
        </p>
      ) : null}
      {info !== null ? (
        <p role="status" className="mt-4 font-sans text-small text-ink-2">
          {info}
        </p>
      ) : null}

      {loading ? (
        <p className="mt-6 font-sans text-body text-muted">Loading usage…</p>
      ) : payload === null ? (
        <p className="mt-6 font-sans text-body text-muted">No data.</p>
      ) : (
        <>
          {/* Token metadata */}
          <section aria-labelledby="token-meta-heading" className="mt-6">
            <Heading level="h2" id="token-meta-heading" className="text-h3 text-ink">
              Token
            </Heading>
            <Card variant="default" className="mt-3 p-4">
              <dl className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Label
                  </dt>
                  <dd className="mt-1 font-sans text-body text-ink">
                    {payload.token.label ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Embed enabled
                  </dt>
                  <dd className="mt-1 font-sans text-body text-ink">
                    {payload.token.is_embed_anonymous ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Allowed embed domains
                  </dt>
                  <dd className="mt-1 flex flex-wrap gap-2">
                    {payload.token.allowed_embed_domains.length === 0 ? (
                      <span className="font-sans text-body text-muted">—</span>
                    ) : (
                      payload.token.allowed_embed_domains.map((d) => (
                        <Badge key={d} variant="neutral">
                          {d}
                        </Badge>
                      ))
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Revoked
                  </dt>
                  <dd className="mt-1 font-sans text-body text-ink">
                    {isRevoked ? formatLocalDateTime(payload.token.revoked_at!) : '—'}
                  </dd>
                </div>
              </dl>
            </Card>
          </section>

          {/* Usage panel */}
          <section aria-labelledby="usage-heading" className="mt-8">
            <div className="flex items-center justify-between">
              <Heading level="h2" id="usage-heading" className="text-h3 text-ink">
                Embed usage (last 7 days)
              </Heading>
              {!isRevoked && outOfAllowlist.length > 0 ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={onRevoke}
                  disabled={revoking}
                  aria-label="Revoke share token"
                >
                  {revoking ? 'Revoking…' : 'Revoke token'}
                </Button>
              ) : null}
            </div>

            <Card variant="default" className="mt-3 p-4">
              <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Total loads (7d)
                  </dt>
                  <dd className="mt-1 font-mono text-h2 text-ink">
                    {String(payload.summary.total_loads_7d)}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Distinct hosts (7d)
                  </dt>
                  <dd className="mt-1 font-mono text-h2 text-ink">
                    {String(payload.summary.distinct_referer_hosts_7d)}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Lifetime loads
                  </dt>
                  <dd className="mt-1 font-mono text-h2 text-ink">
                    {String(payload.summary.embed_load_count_lifetime)}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                    Out-of-allowlist
                  </dt>
                  <dd className="mt-1 font-mono text-h2 text-error">
                    {String(outOfAllowlist.length)}
                  </dd>
                </div>
              </dl>
            </Card>

            {outOfAllowlist.length > 0 ? (
              <Card
                variant="default"
                role="alert"
                className="mt-3 border-error/30 bg-error/5 p-4"
              >
                <Heading level="h3" className="text-h3 text-error">
                  Out-of-allowlist Referers detected
                </Heading>
                <ul className="mt-2 list-inside list-disc font-sans text-body text-ink">
                  {outOfAllowlist.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
                <p className="mt-2 font-sans text-small text-muted">
                  These hosts attempted to load the embed but did not match{' '}
                  <code className="font-mono">allowed_embed_domains</code>. The
                  load was rejected at the CSP / Referer layer; this panel
                  surfaces the attempt so an admin can decide whether to revoke.
                </p>
              </Card>
            ) : null}

            {/* Events table */}
            <Card variant="default" className="mt-4 overflow-hidden p-0">
              <table className="w-full border-collapse text-small">
                <thead>
                  <tr className="bg-bg-warm">
                    <th
                      scope="col"
                      className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Loaded
                    </th>
                    <th
                      scope="col"
                      className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Host
                    </th>
                    <th
                      scope="col"
                      className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      IP (masked)
                    </th>
                    <th
                      scope="col"
                      className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Allowed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payload.events.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center font-sans text-body text-muted"
                      >
                        No loads in the last 7 days.
                      </td>
                    </tr>
                  ) : (
                    payload.events.map((e) => (
                      <tr key={e.id} className="border-b border-line last:border-b-0">
                        <td className="px-4 py-2 font-mono text-mono text-ink-2">
                          {formatLocalDateTime(e.loaded_at)}
                        </td>
                        <td className="px-4 py-2 font-sans text-body text-ink">
                          {e.referer_host ?? '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-mono text-muted">
                          {e.ip_masked ?? '—'}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant={e.allowed ? 'success' : 'error'}>
                            {e.allowed ? 'allowed' : 'rejected'}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Card>
          </section>
        </>
      )}
    </AdminLayout>
  );
}
